import {Component, Event, type EventEmitter, h, Listen, Prop, State, Watch} from '@stencil/core';
import {makeLogger} from '../../../logger/logger';
import type {labelProviderInterface} from '../../LabelProviderInterface';
import type {Autocorrect} from '../../../api/model/response/documents/public/Autocorrect';
import {AutocorrectQueryEvent} from '../../SearchCompletedEvent';

const {warn} = makeLogger({
  pathToCurrentFile: 'components/result-page/results-count/uiaas-search-results-text.tsx',
  loggersList: ['warn']
});

@Component({
  tag: 'uiaas-search-results-header',
  shadow: true,
  styleUrl: 'uiaas-search-results-header.scss',
})
export class SearchResultsHeader {

  /**
   * Provides the component with current query
   */
  @Prop() readonly query: string | undefined = '';

  /**
   * label provider
   */
  @Prop({mutable: true}) labelProvider?: labelProviderInterface;

  @Prop() readonly runAutocorrect: boolean = false;

  /**
   * Provides the component with active search data
   */
  @Prop() readonly activeTabData;

  @Prop() readonly disabled: boolean = false;

  @Prop() readonly resultsHeaderType:'section' | 'subsection' = 'section';

  @State() autocorrect: Autocorrect = {
    totalHits: null,
    suggestion: '',
    enable: null,
  };

  @Watch('query')
  resetAutocorrectData() {
      this.autocorrect.enable = false;
  }

  @Event({
    eventName: 'search:autocorrectquery',
  })
    autocorrectChangeQuery: EventEmitter<AutocorrectQueryEvent>;


  @Listen('search:autocorrect', {target: 'window'})
  searchAutocorrectUpdate(event) {
      if (event.detail.isAutocorrect) {
        this.autocorrect = {
          ...this.autocorrect,
          suggestion: event.detail.suggestion,
          totalHits: event.detail.hasHitsError ? 1 : event.detail.totalHits,
          enable: true
        };
      } else {
        this.autocorrect.suggestion = '';
        this.autocorrect.totalHits = null;
        this.autocorrect.enable = false;
      }
  };


  componentWillLoad() {
      if (!this.labelProvider) {
        this.labelProvider = (name, fallback) => {
            warn(
              'Label provider not defined for the component ',
              this,
              [name, fallback]
            );
            return fallback;
        };
      }
  }
  private get isAutocorrectActive() {
      return this.runAutocorrect && this.autocorrect.enable && !this.disabled;
  }

  private get showAutocorrectButton() {
      return this.runAutocorrect && this.autocorrect.totalHits > 0 && this.autocorrect.enable;
  }

  private addQuotations(term: string | null): string {
      return term?.length ? `"${term}"` : '';
  }

  private handleSubtitle(): string | null {
      return this.runAutocorrect && this.autocorrect.suggestion && this.autocorrect.enable && this.autocorrect.totalHits === 0
        ? `${this.labelProvider('no-results-found-for', 'No results found for')} ${this.addQuotations(this.query)}`
        : null;
  }

  private handleTitle(): string {
      const [key, text, value] = this.isAutocorrectActive
        ? ['showing-results-for', 'Showing results for', this.autocorrect.suggestion]
        : ['search-results-for', 'Search results for', this.query];

      return `${this.labelProvider(key, text)} ${this.addQuotations(value)}`;
  }

  private handleAutocorrectClick = () => {
      this.autocorrectChangeQuery.emit({
        changeQuery: true,
        suggestion: this.autocorrect.suggestion,
        misspelledKeyword: this.query
      });
      this.autocorrect.enable = false;
  };

  render() {
      return (
        <div>
          <qds-web-block-heading
            class="search-results-text__block-heading"
            main-title={this.handleTitle()}
            padding-inline="false"
            platform="desktop"
            layer={this.resultsHeaderType}
            importance={this.resultsHeaderType === 'section' ? 'main' : 'standard'}
            subtitle={this.handleSubtitle()}
            padding-bottom="false"
          />
          {this.showAutocorrectButton && (
            <qds-button
              size="hero"
              importance="subdued"
              class="search__results-text-autocorrect--query-link"
              onClick={this.handleAutocorrectClick}
              text={`${this.labelProvider('search-instead-for', 'Search instead for')} ${this.addQuotations(this.query)}`}
            />
          )}
        </div>
      );
  }
}
